/**
 * sz-26-cj270-sample — CJ270 打样服务 API
 *
 * POST /app-api/activity/cj270-sample/auth
 * POST /app-api/activity/cj270-sample/file/presigned-url
 * PUT  uploadUrl（OSS 直传 XHR：Content-Type / 实际字节数须与预签名 file.type、file.size 完全一致）
 * POST /app-api/activity/cj270-sample/file/multipart/init
 * POST /app-api/activity/cj270-sample/file/multipart/parts
 * PUT  分片 uploadUrl（OSS 直传，保存响应 ETag）
 * POST /app-api/activity/cj270-sample/file/multipart/complete
 * POST /app-api/activity/cj270-sample/file/multipart/abort
 * POST /app-api/activity/cj270-sample/submit
 */
(function (global) {
  'use strict';

  var AUTH_PATH = '/app-api/activity/cj270-sample/auth';
  var PRESIGNED_PATH = '/app-api/activity/cj270-sample/file/presigned-url';
  var MULTIPART_INIT_PATH = '/app-api/activity/cj270-sample/file/multipart/init';
  var MULTIPART_PARTS_PATH = '/app-api/activity/cj270-sample/file/multipart/parts';
  var MULTIPART_COMPLETE_PATH = '/app-api/activity/cj270-sample/file/multipart/complete';
  var MULTIPART_ABORT_PATH = '/app-api/activity/cj270-sample/file/multipart/abort';
  var SUBMIT_PATH = '/app-api/activity/cj270-sample/submit';

  function normalizeBase(baseUrl) {
    var base = String(baseUrl || '').trim();
    if (!base) return '';
    return base.replace(/\/+$/, '');
  }

  function buildApiUrl(baseUrl, path) {
    var base = normalizeBase(baseUrl);
    var combined = base + path;
    if (/^https?:\/\//i.test(combined)) return combined;
    var origin =
      typeof global.location !== 'undefined' && global.location.origin ? global.location.origin : '';
    if (!origin) throw new Error('SZ_CJ270_SAMPLE_API: use absolute https API URL');
    var rel = combined.startsWith('/') ? combined : '/' + combined;
    return new URL(rel, origin).toString();
  }

  function buildJsonHeaders(headers) {
    var reqHeaders = { 'Content-Type': 'application/json' };
    if (headers && headers.tenantId != null && headers.tenantId !== '') {
      reqHeaders['tenant-id'] = String(headers.tenantId);
    }
    if (headers && headers.authorization) {
      reqHeaders['Authorization'] = headers.authorization;
    }
    return reqHeaders;
  }

  function parseJsonResponse(res) {
    return res.text().then(function (text) {
      var json = {};
      try {
        json = text ? JSON.parse(text) : {};
      } catch (e) {
        json = { code: -1, msg: 'Unexpected response' };
      }
      return { httpOk: res.ok, status: res.status, body: json };
    });
  }

  function postJson(url, body, headers) {
    return fetch(url, {
      method: 'POST',
      headers: buildJsonHeaders(headers),
      body: JSON.stringify(body)
    }).then(parseJsonResponse);
  }

  function isSuccess(result) {
    var envelope = result && result.body ? result.body : result;
    return !!(envelope && envelope.code === 0);
  }

  function authSample(baseUrl, body, headers) {
    return postJson(buildApiUrl(baseUrl, AUTH_PATH), body, headers);
  }

  function requestPresignedUrl(baseUrl, body, headers) {
    return postJson(buildApiUrl(baseUrl, PRESIGNED_PATH), body, headers);
  }

  function initMultipart(baseUrl, body, headers) {
    return postJson(buildApiUrl(baseUrl, MULTIPART_INIT_PATH), body, headers);
  }

  function requestMultipartParts(baseUrl, body, headers) {
    return postJson(buildApiUrl(baseUrl, MULTIPART_PARTS_PATH), body, headers);
  }

  function completeMultipart(baseUrl, body, headers) {
    return postJson(buildApiUrl(baseUrl, MULTIPART_COMPLETE_PATH), body, headers);
  }

  function abortMultipart(baseUrl, body, headers) {
    return postJson(buildApiUrl(baseUrl, MULTIPART_ABORT_PATH), body, headers);
  }

  function submitSample(baseUrl, body, headers) {
    return postJson(buildApiUrl(baseUrl, SUBMIT_PATH), body, headers);
  }

  /**
   * OSS 预签名 PUT。必须直传原始 File/Blob：
   * - Content-Type 与申请预签名时的 file.type 完全一致
   * - 浏览器按 body 自动带 Content-Length，等于 file.size，禁止改写文件内容
   *
   * @param {string} uploadUrl
   * @param {File|Blob} file
   * @param {string} mimeType
   * @param {number} [declaredSize]
   * @returns {Promise<{ ok: boolean, status: number }>}
   */
  function uploadToOss(uploadUrl, file, mimeType, declaredSize, onProgress) {
    var contentType = mimeType || 'application/octet-stream';
    var expectedSize = declaredSize != null ? Number(declaredSize) : (file && file.size);
    if (!file) {
      return Promise.reject(new Error('Missing file'));
    }
    if (expectedSize != null && Number(file.size) !== Number(expectedSize)) {
      return Promise.reject(new Error('File size changed. Please select the file again.'));
    }

    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.upload.onprogress = function (e) {
        if (!e.lengthComputable || typeof onProgress !== 'function') return;
        onProgress(e.loaded, e.total || expectedSize);
      };
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ ok: true, status: xhr.status });
          return;
        }
        reject(new Error('OSS upload failed (' + xhr.status + ')'));
      };
      xhr.onerror = function () {
        reject(new Error('OSS upload failed'));
      };
      xhr.onabort = function () {
        reject(new Error('Upload cancelled'));
      };
      xhr.send(file);
    });
  }

  /**
   * 上传一个 OSS Multipart 分片。预签名 URL 已包含签名参数，前端不拼接任何 OSS 参数。
   * ETag 来自 OSS PUT 响应头，complete 时必须原样回传（包括引号）。
   */
  function uploadMultipartPart(uploadUrl, blob, onProgress) {
    if (!blob) return Promise.reject(new Error('Missing upload part'));

    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.upload.onprogress = function (e) {
        if (typeof onProgress !== 'function') return;
        onProgress(e.loaded, e.lengthComputable ? e.total : blob.size);
      };
      xhr.onload = function () {
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(new Error('OSS part upload failed (' + xhr.status + ')'));
          return;
        }
        var etag = xhr.getResponseHeader('ETag');
        if (!etag) {
          reject(new Error('OSS upload succeeded, but the ETag response header is not accessible.'));
          return;
        }
        resolve({ ok: true, status: xhr.status, etag: etag });
      };
      xhr.onerror = function () {
        reject(new Error('OSS part upload failed'));
      };
      xhr.onabort = function () {
        reject(new Error('Upload cancelled'));
      };
      xhr.send(blob);
    });
  }

  global.SZ_CJ270_SAMPLE_API = {
    AUTH_PATH: AUTH_PATH,
    PRESIGNED_PATH: PRESIGNED_PATH,
    MULTIPART_INIT_PATH: MULTIPART_INIT_PATH,
    MULTIPART_PARTS_PATH: MULTIPART_PARTS_PATH,
    MULTIPART_COMPLETE_PATH: MULTIPART_COMPLETE_PATH,
    MULTIPART_ABORT_PATH: MULTIPART_ABORT_PATH,
    SUBMIT_PATH: SUBMIT_PATH,
    buildApiUrl: buildApiUrl,
    authSample: authSample,
    requestPresignedUrl: requestPresignedUrl,
    uploadToOss: uploadToOss,
    initMultipart: initMultipart,
    requestMultipartParts: requestMultipartParts,
    uploadMultipartPart: uploadMultipartPart,
    completeMultipart: completeMultipart,
    abortMultipart: abortMultipart,
    submitSample: submitSample,
    isSuccess: isSuccess
  };
})(typeof window !== 'undefined' ? window : globalThis);
